import { useState, useEffect, useCallback } from 'react';
import useCanteiroAreiaDetalhes from './useCanteiroAreiaDetalhes';
import useCanteiroAreiaAnalise from './useCanteiroAreiaAnalise';
import analistasService, { Analista } from '../../../../services/sementes/analistas';
import canteiroAreiaService from '../../../../services/sementes/canteiroAreia';
import { ImagemFormulario } from '../components/GerenciadorImagens/types';

interface UseFormularioCanteiroAreiaResult {
  detalhes: ReturnType<typeof useCanteiroAreiaDetalhes>['detalhes'];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  analise: ReturnType<typeof useCanteiroAreiaAnalise>;
  imagens: {
    imagens: ImagemFormulario[];
    loading: boolean;
    adicionarImagem: (imagem: Omit<ImagemFormulario, 'id' | 'timestamp'>) => Promise<void>;
    removerImagem: (imagemId: string) => Promise<void>;
  };
  analistas: Analista[];
  loadingAnalistas: boolean;
  searchAnalistas: (query: string) => Promise<Analista[]>;
}

export default function useFormularioCanteiroAreia(id: string): UseFormularioCanteiroAreiaResult {
  const {
    detalhes, loading, error, refetch,
  } = useCanteiroAreiaDetalhes(id);
  const analise = useCanteiroAreiaAnalise();

  const [loadingImagens, setLoadingImagens] = useState(false);
  const [imagensLocais, setImagensLocais] = useState<ImagemFormulario[]>([]);

  const [analistas, setAnalistas] = useState<Analista[]>([]);
  const [analistasCache, setAnalistasCache] = useState<Analista[]>([]);
  const [loadingAnalistas, setLoadingAnalistas] = useState(false);

  useEffect(() => {
    if (detalhes?.imagens) {
      setImagensLocais(detalhes.imagens);
    }
  }, [detalhes?.imagens]);

  useEffect(() => {
    const loadAnalistas = async () => {
      try {
        setLoadingAnalistas(true);
        const { analistas: data } = await analistasService.list(1);
        setAnalistas(data);
        setAnalistasCache(data);
      } catch (analistasError) {
        console.error('Erro ao carregar analistas:', analistasError);
      } finally {
        setLoadingAnalistas(false);
      }
    };
    loadAnalistas();
  }, []);

  const searchAnalistas = useCallback(async (query: string): Promise<Analista[]> => {
    if (!query || query.trim().length === 0) {
      return analistasCache;
    }

    try {
      const { analistas: data } = await analistasService.list(1, query);
      return data;
    } catch (searchError) {
      console.error('Erro ao buscar analistas:', searchError);
      return [];
    }
  }, [analistasCache]);

  const adicionarImagem = useCallback(async (imagem: Omit<ImagemFormulario, 'id' | 'timestamp'>) => {
    if (!id) return;

    setLoadingImagens(true);
    try {
      const novaImagem = await canteiroAreiaService.adicionarImagem(id, imagem);
      const imagemFormulario: ImagemFormulario = {
        id: novaImagem.id,
        url: novaImagem.url,
        nome: novaImagem.nome,
        tipo: novaImagem.tipo,
        timestamp: novaImagem.timestamp,
      };
      setImagensLocais((prevImagens) => [...prevImagens, imagemFormulario]);
    } catch (err) {
      console.error('Erro ao adicionar imagem:', err);
      throw err;
    } finally {
      setLoadingImagens(false);
    }
  }, [id]);

  const removerImagem = useCallback(async (imagemId: string) => {
    if (!id) return;

    setImagensLocais((prevImagens) => prevImagens.filter((img) => img.id !== imagemId));

    setLoadingImagens(true);
    try {
      await canteiroAreiaService.removerImagem(id, imagemId);
    } catch (err) {
      console.error('Erro ao remover imagem:', err);
      if (detalhes?.imagens) {
        setImagensLocais(detalhes.imagens);
      }
      throw err;
    } finally {
      setLoadingImagens(false);
    }
  }, [id, detalhes?.imagens]);

  return {
    detalhes,
    loading,
    error,
    refetch,
    analise,
    imagens: {
      imagens: imagensLocais,
      loading: loadingImagens,
      adicionarImagem,
      removerImagem,
    },
    analistas,
    loadingAnalistas,
    searchAnalistas,
  };
}
